import classNames from 'classnames'
import { isString } from 'lodash'

/** Translations carry a trailing ":"; rows render the label without it. */
export const cleanLabel = label => (isString(label) ? label.replace(/\s*[:：]\s*$/, '') : label)

const ICON_TONE = {
  blue: 'bg-blue-50 text-blue-900',
  sky: 'bg-sky-50 text-sky-800',
  green: 'bg-emerald-50 text-emerald-700',
}

/** White card with an icon header (View Booking sections). */
export const SectionCard = ({ icon: Icon, title, tone = 'blue', right, children, className }) => (
  <section className={classNames('w-full rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-5 shadow-[0_1px_3px_rgba(15,23,42,0.05)]', className)}>
    <div className="mb-4 flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-3">
        {Icon && (
          <span className={classNames('flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl', ICON_TONE[tone] || ICON_TONE.blue)}>
            <Icon className="h-5 w-5" />
          </span>
        )}
        <h3 className="truncate text-[15px] sm:text-base font-bold text-slate-900">{title}</h3>
      </div>
      {right}
    </div>
    {children}
  </section>
)

/** Bordered inner box that holds a card's rows. */
export const Panel = ({ className, children }) => <div className={classNames('rounded-xl border border-slate-200/80 bg-slate-50/30 px-4 py-2', className)}>{children}</div>

/** Uppercase label on the left, value on the right. */
export const Row = ({ label, children, className, valueClassName }) => (
  <div className={classNames('flex flex-col gap-1 border-b border-slate-100 py-2.5 last:border-b-0 sm:flex-row sm:items-start sm:justify-between sm:gap-6', className)}>
    <div className="shrink-0 text-[11px] font-bold uppercase tracking-wider text-slate-700">{cleanLabel(label)}</div>
    <div className={classNames('min-w-0 text-sm text-slate-800 break-words sm:text-right rtl:sm:text-left', valueClassName)}>{children ?? '-'}</div>
  </div>
)

/** Money line: plain label left, amount right; `sign` keeps a +/- gutter so amounts align. */
export const MoneyRow = ({ label, sign, signClassName, bold, children }) => (
  <div className="flex items-start justify-between gap-4 py-1.5">
    <div className={classNames('text-sm', bold ? 'font-bold text-slate-900' : 'text-slate-700')}>{label}</div>
    <div
      className={classNames('flex items-baseline justify-end gap-1.5 text-right tabular-nums rtl:text-left', bold ? 'text-sm font-bold text-slate-900' : 'text-sm text-slate-800')}
    >
      {sign !== undefined && (
        <span className={classNames('w-3 shrink-0 text-center font-semibold', sign ? signClassName : 'invisible')} aria-hidden={!sign}>
          {sign || '+'}
        </span>
      )}
      <span>{children}</span>
    </div>
  </div>
)

const BAND_TONE = {
  slate: 'bg-slate-100/80 text-slate-900',
  sky: 'bg-sky-50 text-sky-900',
  green: 'bg-emerald-50 text-emerald-900',
}

/** Coloured result line at the bottom of a money card (Total / Company Amount / Ajarlee Amount). */
export const Band = ({ label, tone = 'slate', children }) => (
  <div className={classNames('mt-2 flex items-center justify-between gap-4 rounded-lg px-3 py-2.5 text-sm font-bold', BAND_TONE[tone] || BAND_TONE.slate)}>
    <span>{label}</span>
    <span className="tabular-nums">{children}</span>
  </div>
)

/** Class tokens for cards that keep their own row markup. */
export const ROW = 'flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-6 py-2.5 border-b border-slate-100 last:border-b-0'
export const ROW_START = 'flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 sm:gap-6 py-2.5 border-b border-slate-100 last:border-b-0'
export const LABEL = 'shrink-0 text-[11px] font-bold uppercase tracking-wider text-slate-700 sm:min-w-[9rem]'
export const PANEL = 'w-full rounded-xl border border-slate-200/80 bg-slate-50/30 px-4 py-1'
